import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ShieldCheck, AlertTriangle, Mail } from 'lucide-react';

const Terms = () => {
  return (
    <div className="mx-auto mt-16 max-w-4xl bg-white p-6">
      <div className="mb-8 text-center">
        <div className="mb-4 flex items-center justify-center">
          <FileText className="mr-3 h-8 w-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">Terms &amp; Conditions</h1>
        </div>
        <div className="mx-auto h-1 w-24 rounded-full bg-purple-600" />
        <p className="mx-auto mt-4 max-w-2xl text-sm text-gray-600">
          These Terms &amp; Conditions govern your use of Learn With Stack LLC and its services. By accessing or using our website, you agree to
          these terms.
        </p>
      </div>

      <div className="space-y-8">
        <section className="rounded-lg border-l-4 border-purple-600 bg-purple-50 p-6">
          <div className="mb-4 flex items-start">
            <ShieldCheck className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-purple-700" />
            <div>
              <h2 className="mb-3 text-xl font-semibold text-gray-900">1. Use of the Website</h2>
              <p className="leading-relaxed text-gray-700">
                You agree to use this website for lawful purposes only and in a way that does not infringe the rights of others or restrict their
                use of the site. We reserve the right to modify, suspend, or discontinue any part of the website at any time without notice.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-lg border-l-4 border-blue-600 bg-blue-50 p-6">
          <h2 className="mb-3 text-xl font-semibold text-gray-900">2. SMS Communications &amp; Program Description</h2>
          <p className="mb-4 leading-relaxed text-gray-700">
            By providing your phone number through our website forms, you consent to receive SMS messages from Learn With Stack LLC. These
            messages may include:
          </p>
          <ul className="list-disc space-y-2 pl-5 text-gray-700">
            <li>Appointment confirmations and reminders</li>
            <li>Service updates and notifications</li>
            <li>Customer support responses</li>
            <li>Marketing or promotional messages (if applicable)</li>
          </ul>
          <p className="mt-4 text-gray-700">Message frequency may vary.</p>
        </section>

        <section className="rounded-lg border-l-4 border-indigo-600 bg-indigo-50 p-6">
          <h2 className="mb-3 text-xl font-semibold text-gray-900">3. Message &amp; Data Rates</h2>
          <p className="leading-relaxed text-gray-700">
            Message and data rates may apply depending on your mobile carrier and plan.
          </p>
        </section>

        <section className="rounded-lg border-l-4 border-rose-600 bg-rose-50 p-6">
          <h2 className="mb-3 text-xl font-semibold text-gray-900">4. Opt-Out Instructions</h2>
          <p className="mb-3 leading-relaxed text-gray-700">
            You can opt out of SMS communications at any time by replying <span className="font-semibold">STOP</span> to any message.
          </p>
          <p className="leading-relaxed text-gray-700">
            After opting out, you will no longer receive SMS messages unless you opt back in.
          </p>
        </section>

        <section className="rounded-lg border-l-4 border-sky-600 bg-sky-50 p-6">
          <h2 className="mb-3 text-xl font-semibold text-gray-900">5. Help &amp; Support</h2>
          <p className="mb-4 leading-relaxed text-gray-700">
            For help, reply <span className="font-semibold">HELP</span> to any message or contact us at:
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
            <div className="flex items-center">
              <Mail className="mr-2 h-5 w-5 text-sky-700" />
              <a
                href="mailto:admin@learnwithstack.com"
                className="font-medium text-sky-700 transition-colors duration-200 hover:text-sky-900"
              >
                admin@learnwithstack.com
              </a>
            </div>
            <div className="text-gray-700">
              <span className="font-medium">+1 650-484-4920</span>
            </div>
          </div>
        </section>

        <section className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-6">
          <div className="mb-4 flex items-start">
            <AlertTriangle className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-amber-600" />
            <div>
              <h2 className="mb-3 text-xl font-semibold text-gray-900">6. No Guarantees</h2>
              <p className="leading-relaxed text-gray-700">
                All content is provided for general informational purposes only. We make no warranties regarding accuracy, reliability, or
                completeness.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-lg border-l-4 border-gray-700 bg-gray-50 p-6">
          <h2 className="mb-3 text-xl font-semibold text-gray-900">7. Carrier Disclaimer</h2>
          <p className="leading-relaxed text-gray-700">Carriers are not liable for delayed or undelivered messages.</p>
        </section>

        <section className="rounded-lg border-l-4 border-emerald-600 bg-emerald-50 p-6">
          <h2 className="mb-3 text-xl font-semibold text-gray-900">8. Age Requirement</h2>
          <p className="leading-relaxed text-gray-700">
            You must be at least <span className="font-semibold">18 years old</span> to use our services.
          </p>
        </section>

        <section className="rounded-lg border-l-4 border-blue-700 bg-blue-50 p-6">
          <h2 className="mb-3 text-xl font-semibold text-gray-900">9. Privacy Policy</h2>
          <p className="leading-relaxed text-gray-700">
            Your use of our services is also governed by our{' '}
            <Link to="/privacy-policy" className="font-medium text-blue-700 hover:text-blue-900">
              Privacy Policy
            </Link>
            .
          </p>
        </section>

        <section className="rounded-lg border-l-4 border-green-600 bg-green-50 p-6">
          <h2 className="mb-3 text-xl font-semibold text-gray-900">10. Changes to Terms</h2>
          <p className="leading-relaxed text-gray-700">
            We may update these Terms from time to time. Continued use of the website constitutes acceptance of the updated Terms.
          </p>
        </section>
      </div>

      <div className="mt-12 border-t border-gray-200 pt-6">
        <p className="text-center text-sm text-gray-500">
          These terms are effective as of the date of publication and may be updated from time to time.
        </p>
      </div>
    </div>
  );
};

export default Terms;

